import { getCriticalPaths } from "@utilities/getCriticalPaths";
import { getGlobalSettings } from "@utilities/getGlobalSettings";
import { getProgrammeImportData } from "@utilities/getProgrammeImportData";
import { parse, stringify } from "flatted";
import PageComponent from "../components/Page/Page";
import {
  DEFAULT_CONTENTFUL_INCLUDE,
  fetchEntries,
  getPageProps,
} from "../utils/cms/ContentfulService";

const HOMEPAGE_SLUG = "home";

const DEFAULT_REVALIDATE_SECONDS = 60 * 60 * 6; // Revalidate 4 times a day

const contentTypes = [
  "programmeDetails",
  "campaignLandingPage2019",
  "content",
  "eeLandingPage",
  "content2019Page",
  "storyPage",
  "newsDetail",
  "eventsDetail",
  "bsrContentPage",
  "publicationDetail",
  "dpoPage",
  "studentProfileDetail",
  "contributorProfileDetail",
  "facultyProfileDetail",
  "embagTemplate",
  "clusterPage",
  "interactSso",
  "pdfAsset",
  "ResearchDetailsPage",
];

export default function Page({ safeJson }) {
  const { globalContent, pageContent, settings, programmeData, preview } =
    parse(safeJson);

  return (
    <PageComponent
      props={{
        globalContent,
        pageContent,
        settings,
        programmeData,
        preview,
        canonicalUrl:
          pageContent.slug === HOMEPAGE_SLUG ? "/" : `/${pageContent.slug}`,
      }}
    />
  );
}

export async function getStaticPaths() {
  // Call an external API endpoint to get posts

  const allSlugs: string[] = [];

  for (const contentType of contentTypes) {
    const entries = await fetchEntries({
      content_type: contentType,
    });

    allSlugs.push(...entries.map((entry) => entry.fields.slug));
  }

  // Critical pages
  const criticalPaths: Array<string> = await getCriticalPaths();

  const slugsToPrebuild = new Set(
    allSlugs.filter((slug) => {
      return slug !== "404" && slug !== "home" && criticalPaths.includes(slug);
    })
  );

  const paths = Array.from(slugsToPrebuild).map((slug) => ({
    params: {
      slug: slug.split("/"),
    },
  }));

  return { paths, fallback: "blocking" };
}

// During the build, we want to generate a site status file
// This is used by the monitoring system to check if the site is up
// We only want to do this during the build, not on every request
// So we use a function that is only called during the build
// This function is not exported, so it is not called on every request
// It is only called when the file is imported during the build
// We use a dynamic import to avoid issues with Next.js static analysis
const generateSiteStatus = (maintenanceMode) => {
  if (process.env.SITE_STATUS_GENERATED) {
    return;
  }
  process.env.SITE_STATUS_GENERATED = "true";
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { buildSiteStatus } = require("../utils/cms/siteStatus");
  try {
    buildSiteStatus({
      maintenanceMode,
      version: process.env.PACKAGE_VERSION,
      buildDate: process.env.BUILD_DATE,
    });
  } catch (er) {
    console.error("Error generating site status:", er);
  }
};

export async function getStaticProps({ params, preview }) {
  const requestedSlug = params?.slug?.join("/") || HOMEPAGE_SLUG;

  try {
    const { settings, ...otherGlobals } = await getGlobalSettings();

    const { globalContent, pageContent } = await getPageProps({
      slug: requestedSlug,
      contentType: contentTypes,
      globals: [],
      include: DEFAULT_CONTENTFUL_INCLUDE,
      allTags: otherGlobals.tags,
      preview,
    });

    // Contentful calls completed successfully, but no matching entry exists.
    if (!pageContent) {
      return {
        notFound: true,
      };
    }

    generateSiteStatus(!!settings?.siteSettings?.turnOnSiteMaintenanceMode);

    // If this is a programme page
    const programmeData = pageContent.programmeTypeCode
      ? await getProgrammeImportData(pageContent.programmeTypeCode)
      : null;

    // https://github.com/vercel/next.js/discussions/10992#discussioncomment-103826
    const safeJson = stringify({
      globalContent: {
        ...globalContent,
        ...otherGlobals,
      },
      pageContent,
      settings,
      programmeData,
      ...(!!preview && { preview: true }),
    });

    return {
      revalidate: DEFAULT_REVALIDATE_SECONDS,
      props: {
        safeJson,
      },
    };
  } catch (error) {
    console.error(
      `[getStaticProps] Failed to generate page "${requestedSlug}"`,
      error
    );

    throw error;
  }
}
